'use client';

import { Checkbox } from '@kit/ui/checkbox';
import { Label } from '@kit/ui/label';

import type { ComplianceJurisdiction } from '../types/vertical';

interface ConsentCheckboxProps {
  jurisdiction: ComplianceJurisdiction;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  verticalName: string;
  error?: string;
}

/**
 * Consent text templates by jurisdiction.
 * Each returns the exact text that will be stored as the consent record.
 */
function getConsentText(
  jurisdiction: ComplianceJurisdiction,
  verticalName: string,
): string {
  switch (jurisdiction) {
    case 'us-tcpa':
      return `By checking this box, I provide my express written consent to ${verticalName} to contact me by phone calls, text messages (SMS/MMS), and email at the number and email address I have provided, including through the use of automated dialing technology and prerecorded or artificial voice messages. Message and data rates may apply. I understand that my consent is not a condition of any purchase and I may revoke my consent at any time. Reply STOP to any SMS message to opt out of text communications.`;

    case 'eu-gdpr':
      return `I agree to the processing of my personal data as described above for the purpose of receiving communications from ${verticalName}. I understand that I may withdraw my consent at any time by contacting us. For more information, please see our Privacy Policy.`;

    case 'ch-dsg':
      return `Ich stimme der Verarbeitung meiner personenbezogenen Daten durch ${verticalName} zu den oben beschriebenen Zwecken zu. Ich kann meine Einwilligung jederzeit widerrufen. Weitere Informationen finden Sie in unserer Datenschutzerklaerung.`;

    case 'ch-dsg-health':
      return `Ich erteile meine ausdrueckliche Einwilligung zur Verarbeitung meiner personenbezogenen Daten, einschliesslich gesundheitsbezogener Daten, durch ${verticalName} zum Zweck der Pflegevermittlung (gemaess nDSG Art. 6). Ich kann meine Einwilligung jederzeit widerrufen. Meine Daten werden ausschliesslich fuer den angegebenen Zweck verwendet und nicht ins Ausland uebertragen ohne angemessenen Schutz.`;

    case 'za-popia':
      return `By checking this box, I consent to the collection and processing of my personal information in accordance with the Protection of Personal Information Act (POPIA). I understand that I may withdraw my consent at any time by contacting us. My information will be used solely for the purpose of facilitating my driver application and related communications.`;

    case 'ng-ndpr':
      return `By checking this box, I consent to the collection and processing of my personal data in accordance with the Nigeria Data Protection Act (NDPA). I understand my data will be processed for the purpose of facilitating my driver application.`;

    case 'ke-dpa':
      return `By checking this box, I consent to the collection and processing of my personal data in accordance with the Kenya Data Protection Act. I understand I have the right to access, correct, or delete my data.`;

    case 'br-lgpd':
      return `Ao marcar esta caixa, consinto com a coleta e processamento dos meus dados pessoais de acordo com a Lei Geral de Protecao de Dados (LGPD). Entendo que posso revogar meu consentimento a qualquer momento.`;

    case 'in-dpdp':
      return `By checking this box, I consent to the collection and processing of my personal data in accordance with the Digital Personal Data Protection Act (DPDPA). I understand I may withdraw consent at any time.`;

    case 'ph-dpa':
      return `By checking this box, I consent to the collection and processing of my personal information in accordance with the Philippines Data Privacy Act of 2012. I understand my data will be used for driver application purposes.`;

    default: {
      const _exhaustive: never = jurisdiction;
      return _exhaustive;
    }
  }
}

/**
 * Jurisdiction-aware consent checkbox.
 * Renders the appropriate consent text based on the vertical's compliance jurisdiction.
 * The checkbox is NEVER pre-checked (TCPA requirement, applied uniformly).
 * The exact consent text is available via getConsentText() for storage.
 */
export function ConsentCheckbox({
  jurisdiction,
  checked,
  onCheckedChange,
  verticalName,
  error,
}: ConsentCheckboxProps) {
  const consentText = getConsentText(jurisdiction, verticalName);

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex cursor-pointer items-start gap-3"
        onClick={() => onCheckedChange(!checked)}
        role="none"
      >
        <Checkbox
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-invalid={!!error}
          data-test="consent-checkbox"
        />

        <Label className="text-muted-foreground cursor-pointer text-xs leading-relaxed font-normal">
          {consentText}
        </Label>
      </div>

      {error ? (
        <p className="text-destructive text-[0.8rem] font-medium">{error}</p>
      ) : null}
    </div>
  );
}

export { getConsentText };
